/*
 * Whitehouse Lighting & Electrical — Wishlist v1.1
 * Guest-stage compatibility build for the current Shopify theme.
 *
 * Reads legacy product/page hearts (whl-wishlist-v20), preserves their
 * existing product/quotation scripts, and powers /pages/wishlist.
 *
 * IMPORTANT: Customer synchronisation remains OFF until the backend
 * and BOTH legacy heart controllers have been securely integrated.
 * Do not enable the flag solely because a customer has logged in.
 */
(() => {
  'use strict';
  if (window.WhitehouseWishlist) return;

  const KEY = 'wh_wishlist_guest_v1';
  const LEGACY_KEY = 'whl-wishlist-v20';
  const SNAPSHOT_KEY = 'wh_wishlist_legacy_snapshot_v1';
  const PROXY = '/apps/wh-wishlist';
  const ENABLE_CUSTOMER_SYNC = false;
  const MAX = 100;

  const validHandle = value =>
    typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
  const validVariant = value =>
    value == null || /^\d{1,20}$/.test(String(value));

  function clean(value) {
    if (!value || !validHandle(value.handle) || !validVariant(value.variantId)) return null;
    return { handle: value.handle, variantId: value.variantId == null ? null : String(value.variantId) };
  }
  const itemKey = item => `${item.handle}::${item.variantId || ''}`;

  function unique(list) {
    const seen = new Map();
    for (const value of Array.isArray(list) ? list : []) {
      const item = clean(value);
      if (item) seen.set(itemKey(item), item);
    }
    return [...seen.values()];
  }

  function announce(message) {
    const target = document.getElementById('wh-wishlist-status');
    if (target) target.textContent = message;
    if (message) console.info('[Whitehouse Wishlist]', message);
  }

  function readJSON(key, defaultValue) {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return defaultValue;
      const value = JSON.parse(raw);
      return Array.isArray(value) ? value : null;
    } catch {
      return null; // Do not overwrite malformed or inaccessible saved data.
    }
  }

  function writeJSON(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      announce('Browser storage is unavailable. Your wishlist could not be saved.');
      return false;
    }
  }

  function handleFromLegacy(record) {
    if (validHandle(record?.handle)) return record.handle;
    if (typeof record?.url !== 'string') return null;
    try {
      const url = new URL(record.url, location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return null;
      // Existing legacy scripts save the Shopify product URL.
      const parts = url.pathname.split('/');
      const index = parts.lastIndexOf('products');
      if (index < 0 || !parts[index + 1]) return null;
      const handle = decodeURIComponent(parts[index + 1]);
      return validHandle(handle) ? handle : null;
    } catch {
      return null;
    }
  }

  function legacyEntries(raw) {
    const result = [];
    for (const record of raw) {
      const handle = handleFromLegacy(record);
      const id = record?.productId == null ? '' : String(record.productId).trim();
      if (handle && /^\d+$/.test(id)) result.push({ id, handle });
    }
    return result;
  }

  function snapshotIsValid(list) {
    return Array.isArray(list) && list.every(entry =>
      entry && /^\d+$/.test(String(entry.id)) && validHandle(entry.handle)
    );
  }

  /*
   * Bridge old hearts to the modern guest format without erasing legacy data.
   * Snapshot tracks legacy add/remove actions. An old-heart removal removes
   * the corresponding product from the new format too, including after reload.
   */
  function reconcileLegacy() {
    const legacy = readJSON(LEGACY_KEY, []);
    const modern = readJSON(KEY, []);
    const previous = readJSON(SNAPSHOT_KEY, null);

    if (legacy === null || modern === null || (previous !== null && !snapshotIsValid(previous))) {
      announce('A saved wishlist could not be read. No stored products have been overwritten.');
      return unique(modern || []);
    }

    const current = legacyEntries(legacy);
    const old = previous || [];
    let next = unique(modern);

    if (previous !== null) {
      const currentById = new Map(current.map(entry => [entry.id, entry.handle]));
      const removed = new Set(old.filter(entry => currentById.get(entry.id) !== entry.handle).map(entry => entry.handle));
      if (removed.size) next = next.filter(item => !removed.has(item.handle));
    }

    // Initial import, plus any products newly saved with the original hearts.
    const oldIds = new Map(old.map(entry => [entry.id, entry.handle]));
    for (const entry of current) {
      if (previous === null || oldIds.get(entry.id) !== entry.handle) {
        if (!next.some(item => item.handle === entry.handle)) {
          next.push({ handle: entry.handle, variantId: null });
        }
      }
    }
    next = unique(next);

    const changed = JSON.stringify(unique(modern)) !== JSON.stringify(next);
    if (!changed || writeJSON(KEY, next)) {
      writeJSON(SNAPSHOT_KEY, current);
    }
    return next;
  }

  let items = [];
  let mode = 'guest';
  let initialized = false;
  let queue = Promise.resolve();

  function emit() {
    const selected = new Set(items.map(itemKey));
    const generic = new Set(items.filter(item => item.variantId === null).map(item => item.handle));
    document.querySelectorAll('[data-wh-wishlist-toggle]').forEach(button => {
      const entry = clean({handle: button.dataset.handle, variantId: button.dataset.variantId || null});
      if (!entry) return;
      const active = selected.has(itemKey(entry)) || generic.has(entry.handle);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
      button.classList.toggle('is-saved', active);
    });
    document.querySelectorAll('[data-wh-wishlist-count]').forEach(element => {
      element.textContent = String(items.length);
    });
    window.dispatchEvent(new CustomEvent('wh:wishlist:change', {
      detail: {count: items.length, items: [...items], mode}
    }));
  }

  async function api(method, body) {
    const response = await fetch(PROXY, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: body ? {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'} : {},
      body: body ? JSON.stringify(body) : undefined
    });
    if (!response.ok) throw new Error(`Wishlist service error (${response.status})`);
    const payload = await response.json();
    if (!payload || typeof payload !== 'object' || !Array.isArray(payload.items)) {
      throw new Error('Invalid wishlist server response');
    }
    return payload;
  }

  const ready = (async () => {
    items = reconcileLegacy();

    // Do not turn this on until authenticated identity and both old hearts
    // have been migrated to the same customer-aware controller.
    if (ENABLE_CUSTOMER_SYNC) {
      try {
        const response = await api('GET');
        if (response.authenticated === true) {
          const pending = [...items];
          const serverItems = unique(response.items);
          if (pending.length) {
            const result = await api('POST', {action: 'merge', items: pending});
            const merged = unique(result.items);
            const confirmed = result.authenticated === true &&
              pending.every(entry => merged.some(item => itemKey(item) === itemKey(entry)));
            if (!confirmed) throw new Error('Server did not confirm every saved item');
            // Only clear browser data after authenticated server confirmation.
            // Legacy hearts must be migrated before enabling this block.
            if (!writeJSON(KEY, []) || !writeJSON(LEGACY_KEY, []) || !writeJSON(SNAPSHOT_KEY, [])) {
              throw new Error('Could not clear transferred browser wishlist');
            }
            items = merged;
          } else {
            items = serverItems;
          }
          mode = 'customer';
        }
      } catch {
        items = reconcileLegacy();
        mode = 'guest';
        announce('Account synchronisation is unavailable. Your saved items remain in this browser.');
      }
    }

    initialized = true;
    emit();
    return [...items];
  })();

  function saveGuest(next, action, target) {
    // Protect the original stored products: write modern storage first.
    const normalized = unique(next);
    if (!writeJSON(KEY, normalized)) return false;

    // Removing the final saved variant of a product also removes the old-key
    // item, so the original heart won't re-add it after a browser refresh.
    if (action === 'remove' && !normalized.some(item => item.handle === target.handle)) {
      const old = readJSON(LEGACY_KEY, []);
      if (old !== null) {
        const updated = old.filter(record => handleFromLegacy(record) !== target.handle);
        if (updated.length !== old.length && !writeJSON(LEGACY_KEY, updated)) return false;
        // Record our own legacy deletion so it isn't mistaken for a second
        // independent deletion the next time the legacy bridge runs.
        writeJSON(SNAPSHOT_KEY, legacyEntries(updated));
      }
    }
    return true;
  }

  function operation(action, raw) {
    const target = clean(raw);
    if (!target) return Promise.reject(new Error('Invalid product handle or variant ID'));

    const result = queue.catch(() => {}).then(async () => {
      await ready;
      if (mode === 'guest') items = reconcileLegacy();

      const exists = items.some(item => itemKey(item) === itemKey(target));
      const resolved = action === 'toggle' ? (exists ? 'remove' : 'add') : action;
      if (resolved === 'add' && !exists && items.length >= MAX) {
        throw new Error('Your wishlist has reached its 100-item limit.');
      }

      if (mode === 'customer') {
        const response = await api('POST', {action: resolved, item: target});
        if (response.authenticated !== true) throw new Error('Customer session not confirmed');
        items = unique(response.items);
      } else {
        const next = resolved === 'add'
          ? unique([...items, target])
          : items.filter(item => itemKey(item) !== itemKey(target) &&
              !(target.variantId === null && item.handle === target.handle));
        if (!saveGuest(next, resolved, target)) {
          throw new Error('Your wishlist could not be saved. Please check browser storage.');
        }
        items = next;
      }
      emit();
      return [...items];
    });
    queue = result;
    return result;
  }

  const wishlist = {
    ready,
    list: async () => { await ready; await queue.catch(() => {}); return [...items]; },
    add: item => operation('add', item),
    remove: item => operation('remove', item),
    toggle: item => operation('toggle', item),
    refreshHearts: emit,
    get mode() { return mode; }
  };
  window.WhitehouseWishlist = wishlist;

  // The NEW markup uses data-wh-wishlist-toggle. Existing data-whl-card-wish
  // and data-whl-wishlist buttons keep their original click handlers for now.
  document.addEventListener('click', async event => {
    const button = event.target.closest?.('[data-wh-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    if (button.disabled) return;
    button.disabled = true;
    try {
      await wishlist.toggle({handle: button.dataset.handle, variantId: button.dataset.variantId || null});
    } catch (error) {
      announce(error.message || 'Unable to update wishlist.');
    } finally {
      button.disabled = false;
    }
  }, true);

  function refreshFromLegacy() {
    if (mode !== 'guest') return;
    items = reconcileLegacy();
    emit();
  }

  // Collection heart script already dispatches this custom event.
  window.addEventListener('whl:wishlist-updated', refreshFromLegacy);
  // For other tabs, watch both storage formats and the migration snapshot.
  window.addEventListener('storage', event => {
    if ([KEY, LEGACY_KEY, SNAPSHOT_KEY].includes(event.key) && mode === 'guest') {
      refreshFromLegacy();
    }
  });

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }

  function money(cents) {
    const currency = window.Shopify?.currency?.active || 'ZAR';
    const value = Number(cents) / 100;
    try {
      return new Intl.NumberFormat('en-ZA', {style: 'currency', currency}).format(value);
    } catch {
      return `R ${value.toFixed(2)}`;
    }
  }

  function route(path) {
    const base = window.Shopify?.routes?.root || '/';
    return base.replace(/\/$/, '/') + path.replace(/^\//, '');
  }

  async function fetchProduct(item) {
    const response = await fetch(route(`products/${encodeURIComponent(item.handle)}.js`), {
      credentials: 'same-origin', cache: 'no-store'
    });
    if (!response.ok) throw new Error('Product is unavailable');
    return response.json();
  }

  function productCard(item, product) {
    const article = element('article', 'wh-card');
    const remove = element('button', 'wh-card__remove', 'Remove');
    remove.type = 'button';
    remove.addEventListener('click', () => wishlist.remove(item).catch(error => announce(error.message)));

    if (!product) {
      article.append(element('p', 'wh-card__name', 'This saved product is no longer available.'));
      article.append(remove);
      return article;
    }

    const variant = product.variants?.find(v => String(v.id) === item.variantId) ||
      (item.variantId === null ? product.variants?.[0] : null);
    const link = element('a', 'wh-card__link');
    link.href = route(`products/${encodeURIComponent(item.handle)}${variant ? `?variant=${variant.id}` : ''}`);
    if (product.featured_image) {
      const image = element('img', 'wh-card__image');
      image.loading = 'lazy';
      image.alt = product.title;
      image.src = product.featured_image;
      link.append(image);
    }
    link.append(element('h3', 'wh-card__name', product.title));
    article.append(link);
    if (variant) article.append(element('p', 'wh-card__price', money(variant.price)));
    article.append(element('p', 'wh-card__availability', variant?.available ? 'In stock' : 'Unavailable'));

    const actions = element('div', 'wh-card__actions');
    const cart = element('button', 'wh-card__cart', 'Add to cart');
    cart.type = 'button';
    cart.disabled = !variant?.available;
    cart.addEventListener('click', async () => {
      cart.disabled = true;
      cart.textContent = 'Adding…';
      try {
        const response = await fetch(route('cart/add.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({id: variant.id, quantity: 1})
        });
        if (!response.ok) throw new Error('Could not add this product to your cart');
        location.assign(route('cart'));
      } catch (error) {
        cart.disabled = false;
        cart.textContent = 'Try again';
        announce(error.message);
      }
    });
    actions.append(cart, remove);
    article.append(actions);
    return article;
  }

  let renderToken = 0;
  async function renderPage() {
    const grid = document.getElementById('wh-wishlist-grid');
    if (!grid) return;
    const token = ++renderToken;
    const saved = await wishlist.list();
    if (token !== renderToken) return;

    const empty = document.getElementById('wh-wishlist-empty');
    if (empty) empty.hidden = saved.length > 0;
    grid.replaceChildren();
    if (!saved.length) return;

    for (let i = 0; i < saved.length; i += 6) {
      const batch = saved.slice(i, i + 6);
      const results = await Promise.all(batch.map(item => fetchProduct(item).catch(() => null)));
      if (token !== renderToken) return;
      batch.forEach((item, index) => grid.append(productCard(item, results[index])));
    }
  }

  function initialisePage() {
    emit();
    renderPage().catch(() => announce('Unable to display saved products. Please refresh.'));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialisePage, {once: true});
  } else {
    initialisePage();
  }
  window.addEventListener('wh:wishlist:change', () => {
    if (initialized) renderPage().catch(() => announce('Unable to refresh your wishlist.'));
  });
})();
